import { t } from '@lingui/core/macro';
import { BookingRecordDetailsCustomer } from '@navio/nsdk';
import { NoddiStepper, StepperStep } from 'noddi-ui-web';
import { customerTimelineWheelPickups, getIsWindShieldRepairJob, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { getLongAddressName } from 'noddi-web';
import { RefObject } from 'react';
import { getIsVisitClosed } from '../../utils/bookingVisit';
import { BookingEstimatedArrivalInfo } from './BookingEstimatedArrivalInfo';
import { getCompletedStep } from './completedStep';
import { getConfirmedStepDescription } from './confirmedStep';
import { getTirePickupStep } from './tirePickupStep';
import { ViewFeedbackModal } from './ViewFeedbackModal';
import { WheelPickupSigningCard } from './WheelPickupSigningCard';

enum BookingStatusStep {
  Confirmed = 'Confirmed',
  WheelPickupSigning = 'Wheel pickup signing',
  TirePickup = 'Tire pickup',
  Assigned = 'Assigned',
  ServiceInProgress = 'Service in progress',
  DamageReport = 'Damage report',
  FeedbackGiven = 'Feedback given',
  Completed = 'Completed'
}

function getStepsWithTranslation({
  booking,
  activeStepEnum,
  paymentButtonRef
}: {
  booking: BookingRecordDetailsCustomer;
  activeStepEnum: BookingStatusStep;
  paymentButtonRef?: RefObject<HTMLDivElement | null>;
}): StepperStep[] {
  const isWindShieldRepair = getIsWindShieldRepairJob(booking);

  const arrivedAt = booking.startedAt ? noddiFormatDate(booking.startedAt, NoddiDateFormats.TIME) : null;
  const isVisitOver = !!booking.startedAt || getIsVisitClosed(booking);

  const isAssignedStepActive = activeStepEnum === BookingStatusStep.Assigned;
  const isServiceInProgressStepActive = activeStepEnum === BookingStatusStep.ServiceInProgress;

  // Check if wheel pickup signing is needed
  const wheelPickupSigningOrders = booking?.wheelSetPickups || [];
  const hasWheelPickupSigning = wheelPickupSigningOrders.length > 0;
  const lastDocument = wheelPickupSigningOrders
    .sort((a, b) => new Date(b.signingUrlExpiresAt).getTime() - new Date(a.signingUrlExpiresAt).getTime())
    .find((order) => order);
  const hasSigned = !!lastDocument?.documentsSigned.length;
  const signingUrl = lastDocument?.signingUrl;
  const showPoaContent = !hasSigned && signingUrl && new Date(lastDocument?.signingUrlExpiresAt) > new Date();

  const hasGivenFeedback = !!booking.serviceFeedback;

  // Get pickup location for power of attorney
  const pickupLocation =
    lastDocument?.tireHotelLocation?.name ||
    (lastDocument?.pickupAddress ? getLongAddressName(lastDocument.pickupAddress) : null);

  // Power of attorney description helpers
  const getPoaDescription = () => {
    if (showPoaContent) {
      return pickupLocation
        ? t`Please sign the power of attorney to pick up your tires from ${pickupLocation}.`
        : t`Please sign the power of attorney to pick up your tires.`;
    }
    return pickupLocation
      ? t`Power of attorney has been signed to pick up your tires from ${pickupLocation}.`
      : t`Power of attorney has been signed to pick up your tires.`;
  };

  const tirePickupStep = getTirePickupStep({ booking, key: BookingStatusStep.TirePickup });

  const steps: StepperStep[] = [
    {
      label: t`Booking confirmed`,
      description: getConfirmedStepDescription(booking),
      key: BookingStatusStep.Confirmed
    },
    ...(hasWheelPickupSigning
      ? [
          {
            label: t`Power of attorney`,
            description: getPoaDescription(),
            key: BookingStatusStep.WheelPickupSigning,
            content: <WheelPickupSigningCard wheelSetPickups={booking.wheelSetPickups} variant='button' />
          }
        ]
      : []),
    ...(tirePickupStep ? [tirePickupStep] : []),
    ...(isWindShieldRepair
      ? [
          {
            label: t`Damage report`,
            description: t`You will receive a damage report shortly. Make sure to fill it out before we arrive.`,
            key: BookingStatusStep.DamageReport
          }
        ]
      : []),
    // Without a customer visit nobody arrives: the tire pickup is the only physical step.
    ...(booking.hasCustomerVisit
      ? [
          {
            // eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term
            label: arrivedAt ? t`Technician arrived` : t`Estimated arrival`,
            description: arrivedAt ? t`Arrived at ${arrivedAt}.` : undefined,
            key: BookingStatusStep.Assigned,
            isLive: isAssignedStepActive,
            // An estimate only means something before the visit: once it starts, ends or is canceled
            // it would compare the real times to the told window.
            content: isVisitOver ? undefined : (
              <BookingEstimatedArrivalInfo booking={booking} isActiveStep={isAssignedStepActive} />
            )
          },
          {
            label: t`Service in progress`,
            description: getServiceInProgressDescription(booking),
            key: BookingStatusStep.ServiceInProgress,
            isLive: isServiceInProgressStepActive
          }
        ]
      : []),
    getCompletedStep({ booking, key: BookingStatusStep.Completed, paymentButtonRef }),
    ...(hasGivenFeedback
      ? [
          {
            label: t`Feedback given`,
            description: t`Thank you for sharing your experience with us!`,
            key: BookingStatusStep.FeedbackGiven,
            content: <ViewFeedbackModal feedback={booking.serviceFeedback!} />
          }
        ]
      : [])
  ];

  return steps;
}

// Written for where the visit stands, so a step ahead never reads as already happened.
function getServiceInProgressDescription(booking: BookingRecordDetailsCustomer): string | undefined {
  const { startedAt, completedAt } = booking;

  if (startedAt && completedAt) {
    const startTime = noddiFormatDate(startedAt, NoddiDateFormats.TIME);
    const endTime = noddiFormatDate(completedAt, NoddiDateFormats.TIME);
    return t`Started at ${startTime} and finished at ${endTime}.`;
  }
  // Canceled after the start, or completed by status alone: the work is not ongoing.
  if (getIsVisitClosed(booking)) {
    return undefined;
  }
  if (startedAt) {
    // eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term
    return t`Our technician has arrived and is working on your vehicle.`;
  }
  // eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term
  return t`Our technician starts working on your vehicle as soon as they arrive.`;
}

function getActiveStep(booking: BookingRecordDetailsCustomer): BookingStatusStep {
  const {
    communicatedArrivalEnd,
    communicatedArrivalStart,
    wheelSetPickups,
    isFullyUnableToComplete,
    isPartiallyUnableToComplete,
    startedAt,
    serviceFeedback
  } = booking;

  // Check if wheel pickup signing is needed and not signed yet
  if (wheelSetPickups && wheelSetPickups?.length > 0) {
    const lastDocument = wheelSetPickups
      .sort((a, b) => new Date(b.signingUrlExpiresAt).getTime() - new Date(a.signingUrlExpiresAt).getTime())
      .find((order) => order);
    const hasSigned = !!lastDocument?.documentsSigned.length;
    const signingUrl = lastDocument?.signingUrl;
    const showPoaContent = !hasSigned && signingUrl && new Date(lastDocument?.signingUrlExpiresAt) > new Date();

    if (showPoaContent) {
      return BookingStatusStep.WheelPickupSigning;
    }
  }

  const isBookingClosed = isFullyUnableToComplete || isPartiallyUnableToComplete || getIsVisitClosed(booking);
  const hasArrivalNews = startedAt || (communicatedArrivalEnd && communicatedArrivalStart);
  if (customerTimelineWheelPickups(booking).length > 0 && !isBookingClosed && !hasArrivalNews) {
    return BookingStatusStep.TirePickup;
  }

  // If service is completed or cancelled or unable to complete
  if (isBookingClosed) {
    // If feedback has been given, show that as the active step
    if (serviceFeedback) {
      return BookingStatusStep.FeedbackGiven;
    }
    // Otherwise show the completed step (which may include give feedback action)
    return BookingStatusStep.Completed;
  }

  if (startedAt) {
    return BookingStatusStep.ServiceInProgress;
  }
  if (communicatedArrivalEnd && communicatedArrivalStart) {
    return BookingStatusStep.Assigned;
  }
  if (getIsWindShieldRepairJob(booking)) {
    return BookingStatusStep.DamageReport;
  }
  return BookingStatusStep.Confirmed;
}

interface BookingStatusUpdatesProps {
  booking: BookingRecordDetailsCustomer;
  paymentButtonRef?: RefObject<HTMLDivElement | null>;
}

export const BSlugStatusUpdates = ({ booking, paymentButtonRef }: BookingStatusUpdatesProps) => {
  const activeStepEnum = getActiveStep(booking);
  const steps = getStepsWithTranslation({ booking, activeStepEnum, paymentButtonRef });
  const activeStep = steps.findIndex((step) => step.key.includes(activeStepEnum));

  return <NoddiStepper steps={steps} activeStep={activeStep} orientation='vertical' />;
};
